import React, { useState, useMemo } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Search, Filter, X, Clock, Tag, BookOpen, Calculator } from 'lucide-react';
import { mockArticles, categories, popularTags, miniSimulators } from '../../data/mock';
import { fullArticles } from '../../data/fullArticles';
import { Link } from 'react-router-dom';

const AdvancedSearch = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchType, setSearchType] = useState('all'); // all, articles, simulators
  const [sortBy, setSortBy] = useState('relevance'); // relevance, date, readTime
  const [showFilters, setShowFilters] = useState(false);

  // Combine articles avec contenu complet
  const enrichedArticles = mockArticles.map(article => ({
    ...article,
    fullContent: fullArticles[article.slug]?.fullContent || article.content
  }));

  const searchResults = useMemo(() => {
    let results = {
      articles: [],
      simulators: []
    };

    const query = searchQuery.toLowerCase().trim();

    // Recherche dans les articles
    if (searchType === 'all' || searchType === 'articles') {
      results.articles = enrichedArticles.filter(article => {
        // Filtre par catégorie
        if (selectedCategory !== 'all' && article.category !== categories.find(c => c.id === selectedCategory)?.name) {
          return false;
        }

        // Filtre par tags sélectionnés
        if (selectedTags.length > 0 && !selectedTags.some(tag => article.tags.includes(tag))) {
          return false;
        }

        // Recherche textuelle
        if (!query) return true;

        return (
          article.title.toLowerCase().includes(query) ||
          article.excerpt.toLowerCase().includes(query) ||
          article.content.toLowerCase().includes(query) ||
          article.fullContent?.toLowerCase().includes(query) ||
          article.category.toLowerCase().includes(query) ||
          article.tags.some(tag => tag.toLowerCase().includes(query))
        );
      });
    }

    // Recherche dans les simulateurs
    if (searchType === 'all' || searchType === 'simulators') {
      results.simulators = miniSimulators.filter(simulator => {
        if (!query) return searchType === 'simulators';

        return (
          simulator.title.toLowerCase().includes(query) ||
          simulator.description.toLowerCase().includes(query) ||
          simulator.category.toLowerCase().includes(query)
        );
      });
    }

    // Tri des résultats
    if (sortBy === 'date') {
      results.articles.sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
    } else if (sortBy === 'readTime') {
      results.articles.sort((a, b) => a.readTime - b.readTime);
    }

    return results;
  }, [searchQuery, selectedCategory, selectedTags, searchType, sortBy, enrichedArticles]);

  const handleTagToggle = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedTags([]);
    setSearchType('all');
    setSortBy('relevance');
  };

  const totalResults = searchResults.articles.length + searchResults.simulators.length;

  return (
    <div className="max-w-7xl mx-auto p-6 pt-24">
      {/* Header de recherche */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Recherche avancée</h1>
        <p className="text-gray-600">
          Trouvez rapidement les guides et simulateurs qui vous intéressent
        </p>
      </div>

      {/* Barre de recherche principale */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex gap-4 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input
              type="text"
              placeholder="Rechercher des articles, guides, simulateurs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 text-lg h-12"
            />
          </div>
          <Button
            onClick={() => setShowFilters(!showFilters)}
            variant="outline"
            className="h-12 px-4"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filtres
          </Button>
        </div>

        {/* Filtres avancés */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Type de contenu</label>
              <Select value={searchType} onValueChange={setSearchType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tout</SelectItem>
                  <SelectItem value="articles">Articles uniquement</SelectItem>
                  <SelectItem value="simulators">Simulateurs uniquement</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Catégorie</label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes catégories</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name} ({category.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Trier par</label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="relevance">Pertinence</SelectItem>
                  <SelectItem value="date">Date de publication</SelectItem>
                  <SelectItem value="readTime">Durée de lecture</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <Button onClick={clearFilters} variant="outline" className="w-full">
                <X className="h-4 w-4 mr-2" />
                Effacer filtres
              </Button>
            </div>
          </div>
        )}

        {/* Tags populaires */}
        <div className="mt-4">
          <p className="text-sm font-medium text-gray-700 mb-2">Tags populaires :</p>
          <div className="flex flex-wrap gap-2">
            {popularTags.map((tag) => (
              <Button
                key={tag}
                variant={selectedTags.includes(tag) ? "default" : "outline"}
                size="sm"
                onClick={() => handleTagToggle(tag)}
                className="text-xs h-7"
              >
                <Tag className="h-3 w-3 mr-1" />
                {tag}
              </Button>
            ))}
          </div>
        </div>

        {/* Filtres actifs */}
        {(selectedTags.length > 0 || selectedCategory !== 'all' || searchType !== 'all') && (
          <div className="mt-4 flex flex-wrap gap-2">
            {selectedTags.map((tag) => (
              <Badge key={tag} variant="secondary" className="text-xs">
                {tag}
                <X 
                  className="h-3 w-3 ml-1 cursor-pointer" 
                  onClick={() => handleTagToggle(tag)}
                />
              </Badge>
            ))}
            {selectedCategory !== 'all' && (
              <Badge variant="secondary" className="text-xs">
                {categories.find(c => c.id === selectedCategory)?.name}
                <X 
                  className="h-3 w-3 ml-1 cursor-pointer" 
                  onClick={() => setSelectedCategory('all')}
                />
              </Badge>
            )}
            {searchType !== 'all' && (
              <Badge variant="secondary" className="text-xs">
                {searchType === 'articles' ? 'Articles' : 'Simulateurs'}
                <X 
                  className="h-3 w-3 ml-1 cursor-pointer" 
                  onClick={() => setSearchType('all')}
                />
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Résultats */}
      <div className="mb-4">
        <p className="text-gray-600">
          {totalResults} résultat{totalResults > 1 ? 's' : ''} trouvé{totalResults > 1 ? 's' : ''}
          {searchQuery && ` pour "${searchQuery}"`}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Résultats principaux */}
        <div className="lg:col-span-3 space-y-6">
          {/* Articles */}
          {searchResults.articles.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-blue-600" />
                Articles ({searchResults.articles.length})
              </h2>
              <div className="space-y-4">
                {searchResults.articles.map((article) => (
                  <Card key={article.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Badge variant="secondary">{article.category}</Badge>
                            <span className="flex items-center text-sm text-gray-500">
                              <Clock className="h-4 w-4 mr-1" />
                              {article.readTime} min
                            </span>
                            <span className="text-sm text-gray-500">
                              {new Date(article.publishedAt).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                          <CardTitle className="text-xl hover:text-blue-600 transition-colors">
                            <Link to={`/article/${article.slug}`}>
                              {article.title}
                            </Link>
                          </CardTitle>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 mb-4 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {article.tags.map((tag) => (
                          <Badge key={tag} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                      <Link to={`/article/${article.slug}`}>
                        <Button variant="outline" size="sm">
                          Lire l'article
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Simulateurs */}
          {searchResults.simulators.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Calculator className="h-5 w-5 text-green-600" />
                Simulateurs ({searchResults.simulators.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {searchResults.simulators.map((simulator) => (
                  <Card key={simulator.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{simulator.icon}</span>
                        <div className="flex-1">
                          <CardTitle className="text-lg">{simulator.title}</CardTitle>
                          <Badge variant="outline" className="mt-1">{simulator.category}</Badge>
                        </div>
                        {simulator.popular && (
                          <Badge className="bg-yellow-100 text-yellow-800">🔥 Populaire</Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 mb-4">{simulator.description}</p>
                      <Link to={`/simulateur/${simulator.id}`}>
                        <Button className="w-full bg-blue-600 hover:bg-blue-700">
                          Utiliser le simulateur
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Aucun résultat */}
          {totalResults === 0 && searchQuery && (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <Search className="h-16 w-16 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Aucun résultat trouvé
              </h3>
              <p className="text-gray-600 mb-4">
                Essayez avec d'autres mots-clés ou supprimez certains filtres
              </p>
              <Button onClick={clearFilters} variant="outline">
                Effacer tous les filtres
              </Button>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Statistiques */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">📊 Statistiques</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Articles disponibles</span>
                <span className="font-semibold">{enrichedArticles.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Simulateurs</span>
                <span className="font-semibold">{miniSimulators.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Catégories</span>
                <span className="font-semibold">{categories.length}</span>
              </div>
              <div className="flex justify-between border-t pt-3">
                <span className="text-gray-600">Résultats actuels</span>
                <span className="font-bold text-blue-600">{totalResults}</span>
              </div>
            </CardContent>
          </Card>

          {/* Suggestions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">💡 Suggestions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Recherches populaires :</p>
                {['TJM freelance', 'SASU vs EURL', 'charges sociales', 'micro-entreprise'].map((suggestion) => (
                  <Button
                    key={suggestion}
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start text-left h-auto p-2"
                    onClick={() => setSearchQuery(suggestion)}
                  >
                    <Search className="h-3 w-3 mr-2" />
                    {suggestion}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdvancedSearch;